import { css } from '@emotion/core'
import { ContentDelete } from '@fs/zion-icon/dist/cjs/icons'
import { Button, ResponsiveTableRow, Separator, H5, DangerButton } from '@fs/zion-ui'
import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DmcResponsiveCheckboxTable } from '../../../common/DmcResponsiveTable'
import { PersonLink, RelationshipLink } from '../../../app/Links'
import { PermissionsContext } from '../../../app/PermissionsContext'
import { DotSeparator } from '../../../metadata/MetaData'
import PersonInfo from '../PersonInfo'
import OwnerId from '../../../common/OwnerId'
import AddRelationships from '../AddRelationships'

const floatRight = css`
  float: right;
`
export default function ChildrenTable({
  currentPerson,
  family,
  onRemoveSelected,
  onRestoreSelected,
  onRemove,
  onRestore,
  accessControls,
  tombstonedChildrenRelationships,
  showTombstonedRelationships,
}) {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const [relationshipsToRemove, setRelationshipsToRemove] = useState([])
  const [relationshipsToRestore, setRelationshipsToRestore] = useState([])
  const childrenRelationshipsCount = family.children ? family.children.length : 0
  const tombstonedChildrenRelationshipsCount = tombstonedChildrenRelationships
    ? tombstonedChildrenRelationships.length
    : 0

  const showSeparator = () => {
    if (childrenRelationshipsCount > 0) {
      return true
    }
    if (tombstonedChildrenRelationshipsCount > 0 && showTombstonedRelationships) {
      return true
    }
    return false
  }
  return (
    <div>
      <H5>
        {t('person.relationships.childrenof_plural', 'Children ({count}) of', {
          count: childrenRelationshipsCount + (showTombstonedRelationships ? tombstonedChildrenRelationshipsCount : 0),
        })}
        {': '}
        <PersonLink personId={currentPerson.id} personName={currentPerson.name} />
        <DotSeparator />
        {currentPerson.id}
        {family.parent && (
          <>
            {t('person.relationships.and', ' and ')}
            <PersonLink personId={family.parent.personId} personName={family.parent.name} />
            <DotSeparator />
            {family.parent.personId}
          </>
        )}{' '}
      </H5>
      {isAuthorized('FtWritePermission') && childrenRelationshipsCount > 0 && relationshipsToRemove.length > 0 && (
        <DangerButton
          id="childCollectiveRemove"
          Icon={ContentDelete}
          onClick={() => onRemoveSelected(relationshipsToRemove)}
        >
          {t('action.removeChildren', 'Remove ({count}) Children', {
            count: relationshipsToRemove.length,
          })}
        </DangerButton>
      )}
      {childrenRelationshipsCount > 0 && (
        <DmcResponsiveCheckboxTable
          id="childTable"
          totalColumnCount={4}
          onSelectionChange={setRelationshipsToRemove}
          atSizeColumns={{
            default: {
              labels: [
                t('child', 'Child'),
                t('owner', 'Owner'),
                t('person.relationships.relationshipid', 'Relationship ID'),
                '',
              ],
              columnWidths: [4, 4, 2, 2],
            },
          }}
        >
          {family.children &&
            family.children.map((relationship) => (
              <ResponsiveTableRow
                key={relationship.relationshipId}
                name={relationship.relationshipId}
                column1={
                  relationship.child && (
                    <PersonInfo
                      key={relationship.child.personId}
                      person={relationship.child}
                      accessControls={accessControls}
                    />
                  )
                }
                column2={
                  relationship.child && (
                    <OwnerId key={relationship.child.personId} ownerInfo={relationship.child?.ownerInfo} />
                  )
                }
                column3={
                  <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="parentchild" />
                }
                column4={
                  <span css={floatRight}>
                    <DangerButton
                      id={`childRemoveBtn_${relationship.relationshipId}`}
                      Icon={ContentDelete}
                      onClick={() => onRemove(relationship.relationshipId, 'parentchild')}
                      disabled={!isAuthorized('FtWritePermission')}
                    >
                      {t('action.remove', 'Remove')}
                    </DangerButton>
                  </span>
                }
              />
            ))}
        </DmcResponsiveCheckboxTable>
      )}
      {showTombstonedRelationships && tombstonedChildrenRelationships && tombstonedChildrenRelationshipsCount > 0 && (
        <>
          <H5 supplementary>
            {t('person.relationships.tombstoned_plural', 'Tombstoned ({count})', {
              count: tombstonedChildrenRelationshipsCount,
            })}
          </H5>
          {isAuthorized('FtWritePermission') &&
            tombstonedChildrenRelationshipsCount > 0 &&
            relationshipsToRestore.length > 0 && (
              <Button
                id="childCollectiveRestore"
                emphasis="medium"
                onClick={() => onRestoreSelected(relationshipsToRestore)}
              >
                {t('action.restoreChildren', 'Restore ({count}) Children', { count: relationshipsToRestore.length })}
              </Button>
            )}
          <DmcResponsiveCheckboxTable
            id="childRestoreTable"
            totalColumnCount={4}
            onSelectionChange={setRelationshipsToRestore}
            atSizeColumns={{
              default: {
                labels: [
                  t('child', 'Child'),
                  t('owner', 'Owner'),
                  t('person.relationships.relationshipid', 'Relationship ID'),
                  '',
                ],
              },
            }}
          >
            {tombstonedChildrenRelationships.map((relationship) => (
              <ResponsiveTableRow
                id={`parent_${relationship.relationshipId}`}
                key={relationship.relationshipId}
                name={relationship.relationshipId}
                column1={
                  relationship.child && (
                    <PersonInfo
                      key={relationship.child.personId}
                      person={relationship.child}
                      accessControls={accessControls}
                    />
                  )
                }
                column2={
                  relationship.child && (
                    <OwnerId key={relationship.child.personId} ownerInfo={relationship.child?.ownerInfo} />
                  )
                }
                column3={
                  <RelationshipLink relationshipId={relationship.relationshipId} relationshipType="parentchild" />
                }
                column4={
                  <span css={floatRight}>
                    <Button
                      id={`childRestoreBtn_${relationship.relationshipId}`}
                      emphasis="medium"
                      onClick={() => onRestore(relationship.relationshipId, 'parentchild')}
                      disabled={!isAuthorized('FtWritePermission')}
                    >
                      {t('action.restore', 'Restore')}
                    </Button>
                  </span>
                }
              />
            ))}
          </DmcResponsiveCheckboxTable>
        </>
      )}
      <AddRelationships relationshipType="children" parent1={currentPerson} parent2={family.parent} />
      {showSeparator() && <Separator size="xl" />}
    </div>
  )
}
